import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import multiMonthPlugin from '@fullcalendar/multimonth';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import { CalendarPlus, ChevronLeft, ChevronRight, GripVertical, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

import { useCalendarEvents, useCreateTask, useProjects, useUpdateTask } from '../hooks/useApi';
import MeetingModal from '../components/MeetingModal';
import TaskModal from '../components/TaskModal';
import {
    Button,
    Card,
    EmptyState,
    ListSkeleton,
    PageHeader,
    SegmentedControl,
    Tooltip,
} from '../components/ui';
import '../styles/fullcalendar.css';

/* --------------------------------------------------------------------------
   Event colour system — every hue carries a meaning, nothing is decorative.
   -------------------------------------------------------------------------- */
const PALETTE = {
    neutral: { accent: '#8a92a2', tint: '#f1f3f6', text: '#4d5666' },
    info: { accent: '#2589c9', tint: '#eaf4fb', text: '#175a87' },
    warning: { accent: '#d98613', tint: '#fdf3e3', text: '#91620d' },
    danger: { accent: '#dc4c3f', tint: '#fdecea', text: '#a8342a' },
    project: { accent: '#0a2a66', tint: '#eef2f9', text: '#0a2a66' },
    meeting: { accent: '#5b4bc4', tint: '#eeecfa', text: '#443597' },
};

const PRIORITY_TONE = {
    critical: 'danger',
    urgent: 'danger',
    high: 'warning',
    medium: 'info',
    low: 'neutral',
};

const EVENT_TYPES = [
    { key: 'task', label: 'Tasks', color: PALETTE.info.accent, hint: 'Coloured by priority' },
    { key: 'project', label: 'Projects', color: PALETTE.project.accent, hint: 'Delivery milestones' },
    { key: 'meeting', label: 'Meetings', color: PALETTE.meeting.accent, hint: 'Scheduled sessions' },
];

/* Templates that can be dragged onto a date to create a task. */
const DRAGGABLE_TEMPLATES = [
    { id: 'tmpl-1', title: 'Task planning', tone: 'info' },
    { id: 'tmpl-2', title: 'Vendor follow-up', tone: 'neutral' },
    { id: 'tmpl-3', title: 'Report preparation', tone: 'warning' },
    { id: 'tmpl-4', title: 'Escalation review', tone: 'danger' },
];

const VIEWS = [
    { value: 'dayGridMonth', label: 'Month' },
    { value: 'timeGridWeek', label: 'Week' },
    { value: 'timeGridDay', label: 'Day' },
    { value: 'multiMonthYear', label: 'Year' },
    { value: 'listWeek', label: 'List' },
];

const formatTime = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const Calendar = () => {
    const navigate = useNavigate();
    const calendarRef = useRef(null);
    const draggableRef = useRef(null);

    const [viewType, setViewType] = useState('dayGridMonth');
    const [calendarTitle, setCalendarTitle] = useState('');
    const [currentRange, setCurrentRange] = useState(() => {
        const now = new Date();
        return {
            start: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
            end: new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString(),
        };
    });
    const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');

    const { data: eventsData, isLoading } = useCalendarEvents({ ...currentRange });
    const { data: projectsData } = useProjects({ limit: 1000 });
    const updateTask = useUpdateTask();
    const createTask = useCreateTask();

    /* ---------------------------------------------------------------- events */
    const calendarEvents = useMemo(() => {
        if (!eventsData) return [];

        const tasks = (eventsData.tasks || []).map((task) => {
            const tone = PRIORITY_TONE[String(task.priority || '').toLowerCase()] || 'neutral';
            const p = PALETTE[tone];
            return {
                id: `task-${task.id}`,
                title: task.title,
                start: task.end_date,
                end: task.end_date,
                backgroundColor: p.tint,
                borderColor: p.tint,
                textColor: p.text,
                extendedProps: {
                    ...task,
                    type: 'task',
                    accent: p.accent,
                    realStart: task.start_date,
                    realEnd: task.end_date,
                },
            };
        });

        const projects = (eventsData.projects || []).map((proj) => {
            const p = PALETTE.project;
            return {
                id: `proj-${proj.id}`,
                title: proj.title,
                start: proj.end_date,
                end: proj.end_date,
                allDay: true,
                backgroundColor: p.tint,
                borderColor: p.tint,
                textColor: p.text,
                extendedProps: {
                    ...proj,
                    type: 'project',
                    accent: proj.color || p.accent,
                    realStart: proj.start_date,
                    realEnd: proj.end_date,
                },
            };
        });

        const meetings = (eventsData.meetings || []).map((meeting) => {
            const p = PALETTE.meeting;
            const startStr = `${meeting.start_date.split('T')[0]}T${meeting.start_time}`;
            const endStr = `${meeting.end_date.split('T')[0]}T${meeting.end_time}`;
            return {
                id: `meeting-${meeting.id}`,
                title: meeting.title,
                start: startStr,
                end: endStr,
                backgroundColor: p.tint,
                borderColor: p.tint,
                textColor: p.text,
                extendedProps: {
                    ...meeting,
                    type: 'meeting',
                    accent: p.accent,
                    realStart: startStr,
                    realEnd: endStr,
                },
            };
        });

        return [...tasks, ...projects, ...meetings];
    }, [eventsData]);

    const upcomingEvents = useMemo(() => {
        const now = new Date();
        return calendarEvents
            .filter((e) => new Date(e.start) >= now)
            .sort((a, b) => new Date(a.start) - new Date(b.start))
            .slice(0, 6);
    }, [calendarEvents]);

    /* --------------------------------------------------------- drag templates */
    useEffect(() => {
        if (!draggableRef.current) return undefined;
        const instance = new Draggable(draggableRef.current, {
            itemSelector: '.mb-draggable',
            eventData: (el) => ({
                title: el.getAttribute('data-title'),
                create: true,
            }),
        });
        return () => instance.destroy();
    }, []);

    /* ------------------------------------------------------------- handlers */
    const handleEventClick = (info) => {
        const { type, id } = info.event.extendedProps;
        if (type === 'task') navigate(`/tasks/${id}`);
        else if (type === 'meeting') navigate(`/meetings/${id}`);
        else navigate(`/projects/${id}`);
    };

    const handleDateClick = (info) => {
        setSelectedDate(info.dateStr);
        setIsMeetingModalOpen(true);
    };

    const handleEventDrop = async (info) => {
        const { type, id } = info.event.extendedProps;
        if (type !== 'task') {
            info.revert();
            return;
        }
        try {
            await updateTask.mutateAsync({
                id,
                data: {
                    start_date: info.event.start.toISOString(),
                    due_date: (info.event.end || info.event.start).toISOString(),
                },
            });
        } catch {
            info.revert();
        }
    };

    const handleExternalDrop = async (info) => {
        const projectId = projectsData?.data?.[0]?.id;
        if (!projectId) {
            toast.error('Create a project before scheduling tasks from a template.');
            return;
        }
        try {
            await createTask.mutateAsync({
                title: info.draggedEl.getAttribute('data-title'),
                start_date: info.date.toISOString(),
                due_date: new Date(info.date.getTime() + 60 * 60 * 1000).toISOString(),
                status: 'todo',
                priority: 'medium',
                project_id: projectId,
            });
            toast.success('Task created');
        } catch {
            toast.error('Could not create the task. Try again.');
        }
    };

    const handleDatesSet = (info) => {
        setCalendarTitle(info.view.title);
        setCurrentRange({ start: info.start.toISOString(), end: info.end.toISOString() });
    };

    const changeView = (view) => {
        setViewType(view);
        calendarRef.current?.getApi().changeView(view);
    };

    const api = () => calendarRef.current?.getApi();

    /* --------------------------------------------------------------- render */
    const renderEventContent = (eventInfo) => {
        const { type, accent, realStart, realEnd } = eventInfo.event.extendedProps;
        const compact = eventInfo.view.type === 'multiMonthYear';
        const start = realStart ? new Date(realStart) : null;
        const end = realEnd ? new Date(realEnd) : null;
        const allDay = eventInfo.event.allDay;

        if (compact) {
            return (
                <div className="flex items-center gap-1 px-1 py-[1px] overflow-hidden">
                    <span
                        aria-hidden="true"
                        className="w-1 h-1 rounded-full shrink-0"
                        style={{ backgroundColor: accent }}
                    />
                    <span className="truncate text-[9px] font-medium">{eventInfo.event.title}</span>
                </div>
            );
        }

        return (
            <div
                className="mb-event-chip flex items-start gap-1.5 px-1.5 py-[3px] rounded-sm overflow-hidden"
                title={`${eventInfo.event.title}${start && !allDay ? ` · ${formatTime(start)}` : ''}`}
            >
                <span
                    aria-hidden="true"
                    className="w-1 self-stretch rounded-full shrink-0 mt-[2px]"
                    style={{ backgroundColor: accent, minHeight: 10 }}
                />
                <span className="min-w-0">
                    <span className="block truncate text-[11px] font-medium leading-tight">
                        {eventInfo.event.title}
                    </span>
                    <span className="block truncate text-[10px] opacity-75 leading-tight">
                        {allDay
                            ? type === 'project'
                                ? 'Project deadline'
                                : 'All day'
                            : start && end
                                ? `${formatTime(start)} – ${formatTime(end)}`
                                : ''}
                    </span>
                </span>
            </div>
        );
    };

    return (
        <>
            <PageHeader
                title="Calendar"
                description="Task deadlines, project milestones and scheduled meetings in one view."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Calendar' }]}
                actions={
                    <>
                        <Button icon={CalendarPlus} onClick={() => { setSelectedDate(''); setIsMeetingModalOpen(true); }}>
                            Schedule meeting
                        </Button>
                        <Button variant="primary" icon={Plus} onClick={() => setIsTaskModalOpen(true)}>
                            New task
                        </Button>
                    </>
                }
            />

            <div className="flex flex-col xl:flex-row gap-4 items-start">
                {/* ------------------------------------------------ utility rail */}
                <aside className="w-full xl:w-[260px] xl:shrink-0 space-y-4">
                    <Card>
                        <div className="px-4 py-3 border-b border-[var(--divider-color)]">
                            <h2 className="text-label font-semibold text-ink-800 uppercase tracking-wide">
                                Event types
                            </h2>
                        </div>
                        <ul className="px-4 py-3 space-y-2">
                            {EVENT_TYPES.map((t) => (
                                <li key={t.key} className="flex items-center gap-2.5">
                                    <span
                                        aria-hidden="true"
                                        className="w-2 h-2 rounded-[2px] shrink-0"
                                        style={{ backgroundColor: t.color }}
                                    />
                                    <span className="text-base text-ink-800">{t.label}</span>
                                    <span className="ml-auto text-micro text-ink-400">{t.hint}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>

                    <Card>
                        <div className="px-4 py-3 border-b border-[var(--divider-color)]">
                            <h2 className="text-label font-semibold text-ink-800 uppercase tracking-wide">
                                Quick add
                            </h2>
                            <p className="text-micro text-ink-500 mt-0.5">
                                Drag a template onto a date to create a task.
                            </p>
                        </div>
                        <div ref={draggableRef} className="px-2 py-2 space-y-0.5">
                            {DRAGGABLE_TEMPLATES.map((tmpl) => (
                                <div
                                    key={tmpl.id}
                                    data-title={tmpl.title}
                                    className="mb-draggable flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-[var(--bg-subtle)] transition-colors duration-150"
                                >
                                    <GripVertical size={13} className="text-ink-300 shrink-0" aria-hidden="true" />
                                    <span
                                        aria-hidden="true"
                                        className="w-1.5 h-1.5 rounded-full shrink-0"
                                        style={{ backgroundColor: PALETTE[tmpl.tone].accent }}
                                    />
                                    <span className="text-base text-ink-700 truncate">{tmpl.title}</span>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card>
                        <div className="px-4 py-3 border-b border-[var(--divider-color)]">
                            <h2 className="text-label font-semibold text-ink-800 uppercase tracking-wide">
                                Upcoming
                            </h2>
                        </div>
                        {isLoading ? (
                            <div className="p-4">
                                <ListSkeleton rows={3} />
                            </div>
                        ) : upcomingEvents.length ? (
                            <ul className="divide-y divide-[var(--divider-color)]">
                                {upcomingEvents.map((event) => {
                                    const date = new Date(event.start);
                                    const { type, accent, id } = event.extendedProps;
                                    const href =
                                        type === 'task'
                                            ? `/tasks/${id}`
                                            : type === 'meeting'
                                                ? `/meetings/${id}`
                                                : `/projects/${id}`;
                                    return (
                                        <li key={event.id}>
                                            <button
                                                type="button"
                                                onClick={() => navigate(href)}
                                                className="w-full text-left flex gap-2.5 px-4 py-2.5 hover:bg-[var(--bg-subtle)] transition-colors duration-150"
                                            >
                                                <span className="w-8 shrink-0 text-center">
                                                    <span className="block text-micro text-ink-500 uppercase">
                                                        {date.toLocaleDateString(undefined, { month: 'short' })}
                                                    </span>
                                                    <span className="block text-md font-semibold text-ink-900 tabular leading-tight">
                                                        {date.getDate()}
                                                    </span>
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex items-center gap-1.5">
                                                        <span
                                                            aria-hidden="true"
                                                            className="w-1.5 h-1.5 rounded-full shrink-0"
                                                            style={{ backgroundColor: accent }}
                                                        />
                                                        <span className="text-base font-medium text-ink-900 truncate">
                                                            {event.title}
                                                        </span>
                                                    </span>
                                                    <span className="block text-micro text-ink-500 mt-0.5 tabular">
                                                        {event.allDay ? 'All day' : formatTime(date)}
                                                        {event.extendedProps.project_name
                                                            ? ` · ${event.extendedProps.project_name}`
                                                            : ''}
                                                    </span>
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <EmptyState
                                compact
                                title="Nothing scheduled"
                                description="Upcoming deadlines and meetings will appear here."
                            />
                        )}
                    </Card>
                </aside>

                {/* --------------------------------------------------- calendar */}
                <Card className="flex-1 min-w-0 w-full flex flex-col">
                    <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-[var(--divider-color)]">
                        <div className="flex items-center gap-1">
                            <Tooltip content="Previous">
                                <button
                                    type="button"
                                    onClick={() => api()?.prev()}
                                    aria-label="Previous period"
                                    className="btn btn-ghost btn-sm btn-icon"
                                >
                                    <ChevronLeft size={15} aria-hidden="true" />
                                </button>
                            </Tooltip>
                            <Tooltip content="Next">
                                <button
                                    type="button"
                                    onClick={() => api()?.next()}
                                    aria-label="Next period"
                                    className="btn btn-ghost btn-sm btn-icon"
                                >
                                    <ChevronRight size={15} aria-hidden="true" />
                                </button>
                            </Tooltip>
                            <Button size="sm" onClick={() => api()?.today()} className="ml-1">
                                Today
                            </Button>
                        </div>

                        <h2 className="text-lg font-semibold text-ink-900 truncate order-last w-full sm:order-none sm:w-auto">
                            {calendarTitle}
                        </h2>

                        <SegmentedControl
                            className="sm:ml-auto"
                            label="Calendar view"
                            options={VIEWS}
                            value={viewType}
                            onChange={changeView}
                        />
                    </div>

                    <div className="mb-calendar relative p-3 sm:p-4">
                        {isLoading && (
                            <div
                                className="absolute inset-0 z-sticky flex items-start justify-center pt-24 bg-white/60"
                                role="status"
                                aria-label="Loading calendar"
                            >
                                <span className="w-6 h-6 rounded-full border-2 border-[var(--border-strong)] border-t-[var(--primary)] animate-spin" />
                            </div>
                        )}
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[
                                multiMonthPlugin,
                                dayGridPlugin,
                                timeGridPlugin,
                                listPlugin,
                                interactionPlugin,
                            ]}
                            initialView="dayGridMonth"
                            headerToolbar={false}
                            events={calendarEvents}
                            editable
                            droppable
                            selectable
                            dayMaxEvents={3}
                            views={{
                                multiMonthYear: { dayMaxEvents: 2 },
                                listWeek: { noEventsContent: 'No events scheduled this week' },
                            }}
                            eventClick={handleEventClick}
                            dateClick={handleDateClick}
                            eventDrop={handleEventDrop}
                            eventResize={handleEventDrop}
                            drop={handleExternalDrop}
                            datesSet={handleDatesSet}
                            height="auto"
                            multiMonthMaxColumns={3}
                            eventDisplay="block"
                            firstDay={1}
                            nowIndicator
                            eventContent={renderEventContent}
                        />
                    </div>
                </Card>
            </div>

            <MeetingModal
                isOpen={isMeetingModalOpen}
                onClose={() => setIsMeetingModalOpen(false)}
                initialDate={selectedDate}
            />
            {isTaskModalOpen && <TaskModal task={null} onClose={() => setIsTaskModalOpen(false)} />}
        </>
    );
};

export default Calendar;
